import { z } from "zod";

const id = z.string().min(1).max(100);
const optionalId = z
  .union([id, z.literal(""), z.null()])
  .optional()
  .transform((value) => (value ? value : null));
const code = z
  .string()
  .trim()
  .regex(/^[A-Z0-9]{1,30}$/i, "编码只能包含字母和数字")
  .optional();

export const companyCreateSchema = z.object({
  code,
  name: z.string().trim().min(1, "请输入公司名称").max(120),
  isActive: z.boolean().optional().default(true),
});
export const companyUpdateSchema = companyCreateSchema.partial().extend({
  name: z.string().trim().min(1).max(120).optional(),
});

export const departmentCreateSchema = z.object({
  companyId: id,
  parentId: optionalId,
  code,
  name: z.string().trim().min(1, "请输入部门名称").max(120),
  isCostCenter: z.boolean().optional().default(false),
  managerUserId: optionalId,
  sortOrder: z.coerce.number().int().min(0).max(9999).optional().default(0),
  isActive: z.boolean().optional().default(true),
});
export const departmentUpdateSchema = departmentCreateSchema.partial();

export const locationCreateSchema = z.object({
  code,
  name: z.string().trim().min(1, "请输入位置名称").max(120),
  companyId: optionalId,
  address: z.string().trim().max(300).optional().nullable(),
  building: z.string().trim().max(80).optional().nullable(),
  floor: z.string().trim().max(40).optional().nullable(),
  room: z.string().trim().max(40).optional().nullable(),
  isActive: z.boolean().optional().default(true),
});
export const locationUpdateSchema = locationCreateSchema.partial();

export const warehouseCreateSchema = z.object({
  code,
  name: z.string().trim().min(1, "请输入仓库名称").max(120),
  companyId: optionalId,
  warehouseType: z.enum(["entity", "virtual", "repair", "other"]).default("other"),
  locationId: optionalId,
  managerUserId: optionalId,
  isActive: z.boolean().optional().default(true),
});
export const warehouseUpdateSchema = warehouseCreateSchema.partial();

export const personnelCreateSchema = z.object({
  employeeNo: z.string().trim().max(40).optional().nullable(),
  name: z.string().trim().min(1, "请输入姓名").max(80),
  companyId: optionalId,
  departmentId: optionalId,
  managerId: optionalId,
  position: z.string().trim().max(80).optional().nullable(),
  phone: z.string().trim().max(40).optional().nullable(),
  email: z.string().trim().email("邮箱格式不正确").max(160).optional().nullable(),
  adid: z.string().trim().max(80).optional().nullable(),
  isActive: z.boolean().optional().default(true),
});
export const personnelUpdateSchema = personnelCreateSchema.partial();

export const materialCategoryCreateSchema = z.object({
  levelId: id,
  code,
  name: z.string().trim().min(1, "请输入类别名称").max(120),
  sortOrder: z.coerce.number().int().min(0).max(9999).optional().default(0),
  isActive: z.boolean().optional().default(true),
});
export const materialCategoryUpdateSchema = materialCategoryCreateSchema.partial();

export const materialProductCreateSchema = z.object({
  categoryId: id,
  code,
  name: z.string().trim().min(1, "请输入品名名称").max(120),
  sortOrder: z.coerce.number().int().min(0).max(9999).optional().default(0),
  isActive: z.boolean().optional().default(true),
});
export const materialProductUpdateSchema = materialProductCreateSchema.partial();

export const materialSpecCreateSchema = z.object({
  productId: id,
  code,
  name: z.string().trim().min(1, "请输入规格配置名称").max(120),
  sortOrder: z.coerce.number().int().min(0).max(9999).optional().default(0),
  isActive: z.boolean().optional().default(true),
});
export const materialSpecUpdateSchema = materialSpecCreateSchema.partial();

export const materialCreateSchema = z.object({
  name: z.string().trim().min(1, "请输入物料名称").max(160),
  levelId: id,
  categoryId: optionalId,
  productId: optionalId,
  specConfigId: optionalId,
  unit: z.string().trim().min(1, "请输入计量单位").max(20).default("个"),
  estimatedUnitPriceYuan: z.coerce.number().min(0).max(100_000_000).optional().nullable(),
  expectedLifeDays: z.coerce.number().int().positive("预计使用期必须大于 0"),
  safetyStock: z.coerce.number().int().min(0).max(1_000_000).optional().default(0),
  companyId: optionalId,
  attributes: z.record(z.string(), z.unknown()).optional().default({}),
  classificationOverrideReason: z.string().trim().max(500).optional().nullable(),
});
export const materialUpdateSchema = materialCreateSchema.partial();

export const basicDataCreateSchema = z.object({
  dataType: z.enum(["brand", "os", "office", "asset_option"]),
  category: z.string().trim().max(80).optional().nullable(),
  code,
  name: z.string().trim().min(1, "请输入名称").max(120),
  companyId: optionalId,
  sortOrder: z.coerce.number().int().min(0).max(9999).optional().default(0),
  isActive: z.boolean().optional().default(true),
});

export type CompanyCreateInput = z.infer<typeof companyCreateSchema>;
export type DepartmentCreateInput = z.infer<typeof departmentCreateSchema>;
export type PersonnelCreateInput = z.infer<typeof personnelCreateSchema>;
export type MaterialCreateInput = z.infer<typeof materialCreateSchema>;

export const userCreateSchema = z.object({
  displayName: z.string().trim().min(1, "请输入姓名").max(80),
  identifierType: z.enum(["phone", "email", "adid"]),
  identifierValue: z.string().trim().min(1, "请输入登录标识").max(200),
  roleIds: z.array(id).min(1, "请至少选择一个角色"),
  initialPassword: z.string().min(12).max(200).optional(),
  isActive: z.boolean().optional().default(true),
});

export const userUpdateSchema = z.object({
  displayName: z.string().trim().min(1).max(80).optional(),
});

export const userRolesUpdateSchema = z.object({
  roleIds: z.array(id).min(1, "请至少选择一个角色"),
});

export const userStatusSchema = z.object({
  isActive: z.boolean(),
});

export const rolePermissionsUpdateSchema = z.object({
  permissionCodes: z.array(z.string().min(1)).max(500),
});

export const personnelMatchSchema = z.object({
  userId: optionalId,
});

export const basicDataUpdateSchema = basicDataCreateSchema.partial();

export const dataScopeCreateSchema = z.object({
  scopeType: z.enum([
    "self",
    "own_department",
    "own_cost_center",
    "own_company",
    "company",
    "department",
    "cost_center",
    "warehouse",
    "all",
  ]),
  scopeId: optionalId,
});

export const approvalDutyCreateSchema = z.object({
  userId: id,
  dutyType: z.literal("cost_center_owner"),
  scopeCompanyId: optionalId,
  scopeCostCenterId: optionalId,
  scopeDepartmentId: optionalId,
  activeTo: z.string().datetime().optional().nullable(),
});

export const importRowsSchema = z.object({
  dataType: z.enum([
    "company",
    "department",
    "personnel",
    "material",
    "brand",
    "os",
    "office",
    "asset_option",
  ]),
  mode: z.enum(["create_only", "upsert"]).default("create_only"),
  dryRun: z.boolean().default(false),
  rows: z.array(z.record(z.string(), z.unknown())).min(1).max(2000),
});
